/**
 * Pipeline Engine
 * 
 * The core orchestrator that executes pipeline steps in sequence.
 * 
 * @see docs/Research/DesignSpecsClaude.md Section 3.3.2
 */

import type {
  IPipelineExecutionContext,
  IPipelineResult,
  IStepResult,
} from '@/interfaces/IPipeline';
import type { ITool, ToolExecutionResult } from '@/interfaces/ITool';
import type { ToolExecutionContext } from '@/interfaces/ITool';
import { isGenerator, isTransformer, isVisualizer, normalizeToolResult, resolveOutputType } from '@/interfaces/ITool';
import { interpolateConfig,interpolateVariables } from '@/lib/utils';

import { PipelineError } from './errors/PipelineError';
import { ToolError } from './errors/ToolError';
import { ToolRegistry } from './ToolRegistry';

/**
 * Pipeline Engine - executes tools in sequence.
 */
export class PipelineEngine {
  /**
   * Execute a pipeline with the given context.
   */
  async execute(context: IPipelineExecutionContext): Promise<IPipelineResult> {
    const startTime = performance.now();
    const stepResults: IStepResult[] = [];

    if (context.steps.length === 0) {
      return {
        success: true,
        output: context.input,
        outputType: context.inputType,
        stepResults: [],
        totalDuration: performance.now() - startTime,
      };
    }
    
    let currentData = context.input;
    let currentType = context.inputType;
    let failed = false;
    let failedStepId: string | undefined;
    let failureError: Error | undefined;
    
    for (const step of context.steps) {
      // Skip remaining steps if already failed
      if (failed) {
        stepResults.push({
          stepId: step.id,
          status: 'skipped',
          input: '',
          output: '',
          duration: 0,
        });
        continue;
      }

      // Record disabled steps as skipped (pass-through)
      if (!step.isEnabled) {
        stepResults.push({
          stepId: step.id,
          status: 'skipped',
          input: currentData,
          output: currentData,
          inputType: currentType,
          outputType: currentType,
          duration: 0,
        });
        continue;
      }
      
      // Notify listener that step is starting
      context.onStepStart?.(step.id);
      
      const stepStartTime = performance.now();
      
      try {
        // Get tool from registry
        const tool = ToolRegistry.get(step.toolId);
        if (!tool) {
          throw PipelineError.toolNotFound(step.toolId, step.id);
        }
        
        // Execute based on tool type
        const variables = context.variables ?? {};
        const toolContext: ToolExecutionContext = {
          pipelineName: context.pipelineName,
          stepId: step.id,
          variables,
        };

        const inputWithVars = interpolateVariables(currentData, variables);
        const configWithVars = interpolateConfig(step.config, variables);

        const result = await this.executeStep(tool, inputWithVars, configWithVars, toolContext);
        
        const stepDuration = performance.now() - stepStartTime;
        
        stepResults.push({
          stepId: step.id,
          status: 'success',
          input: currentData,
          output: result.output,
          inputType: currentType,
          outputType: result.outputType || currentType,
          duration: stepDuration,
        });
        
        // Update current data for next step
        currentData = result.output;
        currentType = result.outputType || currentType;
        
        // Notify listener
        context.onStepComplete?.(step.id, stepResults[stepResults.length - 1]);
        
      } catch (error) {
        failed = true;
        failedStepId = step.id;
        failureError = error instanceof Error ? error : new Error(String(error));
        
        const stepDuration = performance.now() - stepStartTime;
        
        stepResults.push({
          stepId: step.id,
          status: 'error',
          input: currentData,
          output: '',
          inputType: currentType,
          duration: stepDuration,
          error: {
            type: error instanceof ToolError ? error.type : 'ExecutionError',
            message: failureError.message,
            details: error instanceof ToolError ? error.details : undefined,
          },
        });
        
        // Notify listener
        context.onStepError?.(step.id, failureError);
      }
    }
    
    const totalDuration = performance.now() - startTime;
    
    if (failed) {
      return {
        success: false,
        output: '',
        stepResults,
        totalDuration,
        error: {
          stepId: failedStepId!,
          type: failureError instanceof ToolError ? failureError.type : 'ExecutionError',
          message: failureError!.message,
        },
      };
    }
    
    return {
      success: true,
      output: currentData,
      outputType: currentType,
      stepResults,
      totalDuration,
    };
  }

  /**
   * Execute a single step.
   */
  private async executeStep(
    tool: ITool,
    input: string,
    config: Record<string, unknown>,
    context?: ToolExecutionContext
  ): Promise<{ output: string; outputType?: typeof tool.outputType }> {
    if (isGenerator(tool)) {
      const raw = await tool.generate(config, context);
      const fallbackType = resolveOutputType(tool, config);
      return normalizeToolResult(raw, fallbackType);
    }
    
    if (isTransformer(tool)) {
      const raw = await tool.transform(input, config, context);
      const fallbackType = resolveOutputType(tool, config);
      return normalizeToolResult(raw, fallbackType);
    }
    
    if (isVisualizer(tool)) {
      // Visualizers are display-only — pass data AND type through unchanged.
      // toText() exists for reverse-editing scenarios, not pipeline execution.
      // See: docs/exec-plans/v4/architectural-invariants.md
      return { output: input };
    }
    
    // Default fallback — unknown tool types pass through
    return { output: input };
  }

  /**
   * Execute a single tool directly (standalone mode).
   */
  async executeTool(
    toolId: string,
    input: string,
    config: Record<string, unknown> = {}
  ): Promise<ToolExecutionResult> {
    const tool = ToolRegistry.get(toolId);
    if (!tool) {
      throw new PipelineError(`Tool "${toolId}" not found`);
    }
    
    return this.executeStep(tool, input, config);
  }

  /**
   * Re-execute a pipeline from a specific step forward, using an override
   * input for that step. Steps before `fromStepIndex` are NOT re-run;
   * their previous results are preserved.
   *
   * This powers the "dynamic editor" feature: when a user edits data inside
   * an editable visualizer, only downstream steps need to re-execute.
   *
   * @param context          Full pipeline context (all steps, variables, etc.)
   * @param fromStepIndex    0-based index of the step to start re-executing from
   * @param overrideInput    The new input for the step at `fromStepIndex`
   * @param previousResults  Results from a prior full execution (steps before
   *                         `fromStepIndex` are copied as-is)
   * @returns A complete IPipelineResult with merged step results
   */
  async executeFrom(
    context: IPipelineExecutionContext,
    fromStepIndex: number,
    overrideInput: string,
    previousResults: IStepResult[] = [],
  ): Promise<IPipelineResult> {
    const startTime = performance.now();

    // Clamp index
    if (fromStepIndex < 0 || fromStepIndex >= context.steps.length) {
      throw new PipelineError(
        `fromStepIndex ${fromStepIndex} is out of range (0..${context.steps.length - 1})`
      );
    }

    // Build a new context that starts execution at fromStepIndex
    const stepsToRun = context.steps.slice(fromStepIndex);

    // Derive input type from the nearest prior step with a defined outputType
    let priorOutputType = context.inputType;
    if (fromStepIndex > 0 && previousResults.length > 0) {
      for (let i = Math.min(fromStepIndex - 1, previousResults.length - 1); i >= 0; i--) {
        if (previousResults[i]?.outputType) {
          priorOutputType = previousResults[i].outputType;
          break;
        }
      }
    }

    const partialContext: IPipelineExecutionContext = {
      ...context,
      input: overrideInput,
      inputType: priorOutputType,
      steps: stepsToRun,
    };

    const partialResult = await this.execute(partialContext);

    // Merge: keep previous results for steps before fromStepIndex,
    // then append the freshly computed results
    const mergedStepResults: IStepResult[] = [
      ...previousResults.slice(0, fromStepIndex),
      ...partialResult.stepResults,
    ];

    return {
      ...partialResult,
      stepResults: mergedStepResults,
      totalDuration: performance.now() - startTime,
    };
  }
}

// Export singleton instance
export const pipelineEngine = new PipelineEngine();

